import ErrorState from "components/Error/ErrorState";
import type { ErrorInfo, ReactNode } from "react";
import React, { Component } from "react";

import * as Sentry from "@sentry/react";

interface Props {
  children: ReactNode;
  viewState?: string;
  metadocId?: number;
  tabId?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class MetaDocErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { viewState, metadocId, tabId } = this.props;

    // enhanced console logging for dev debugging only
    if (process.env.NODE_ENV === "development") {
      console.error("🔴 [ERROR BOUNDARY CAUGHT ERROR]", {
        error: error.message,
        stack: error.stack,
        viewState,
        metadocId,
        tabId,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
      });
    }

    // log to Sentry for monitoring
    Sentry.captureException(error, {
      tags: {
        feature: "metadoc",
        action: "error_boundary",
        viewState: viewState || "unknown",
        tabId: tabId || "none",
      },
      extra: {
        componentStack: errorInfo.componentStack,
        metadocId,
        viewState,
        tabId,
      },
    });

    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      const isDev = process.env.NODE_ENV === "development";
      const { viewState, metadocId, tabId } = this.props;

      return (
        <div
          style={{
            padding: "2rem",
            backgroundColor: isDev ? "#fee" : undefined,
            border: isDev ? "3px solid red" : undefined,
          }}
        >
          <ErrorState
            description={
              this.state.error?.message ||
              "Failed to load MetaDoc viewer. Please refresh the page."
            }
          />
          {isDev && (
            <div
              style={{
                marginTop: "1rem",
                padding: "1rem",
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontFamily: "monospace",
                fontSize: "12px",
              }}
            >
              <h3 style={{ color: "red", marginTop: 0 }}>
                🔴 Error Boundary Debug Info (Dev Only)
              </h3>
              <div>
                <strong>View State:</strong> {viewState || "unknown"}
              </div>
              <div>
                <strong>Metadoc ID:</strong> {metadocId || "unknown"}
              </div>
              <div>
                <strong>Tab ID:</strong> {tabId || "none"}
              </div>
              <div>
                <strong>Error:</strong> {this.state.error?.message}
              </div>
              <details style={{ marginTop: "1rem" }}>
                <summary style={{ cursor: "pointer", fontWeight: "bold" }}>
                  Stack Trace
                </summary>
                <pre
                  style={{
                    marginTop: "0.5rem",
                    padding: "0.5rem",
                    backgroundColor: "#f5f5f5",
                    overflow: "auto",
                    maxHeight: "200px",
                  }}
                >
                  {this.state.error?.stack}
                </pre>
              </details>
              {this.state.errorInfo && (
                <details style={{ marginTop: "1rem" }}>
                  <summary style={{ cursor: "pointer", fontWeight: "bold" }}>
                    Component Stack
                  </summary>
                  <pre
                    style={{
                      marginTop: "0.5rem",
                      padding: "0.5rem",
                      backgroundColor: "#f5f5f5",
                      overflow: "auto",
                      maxHeight: "200px",
                    }}
                  >
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default MetaDocErrorBoundary;
